import './index.scss'

import { useEffect, useState } from 'react'

import { useAuth } from '@auth/AuthProvider'
import Modal from '@components/Modal'
import { useChurchDataProvider } from '@data/ChurchDataProvider'
import NetworkController from '@data/controller/NetworkController'
import TrainingController from '@data/controller/TrainingController'
import { TrainingModule, TrainingModuleAttributes } from '@interfaces/training.interface'

import closeIcon from 'url:@icon/close.svg';
import checkmarkIcon from 'url:@icon/checkmark.svg';

export default function ModuleEditorModal(props: {
  isOpen: boolean,
  module?: TrainingModule | null,
  close: () => void,
  onSaved: () => void
}) {
  const auth = useAuth();
  const churchData = useChurchDataProvider();
  const attributes = props.module?.attributes;
  const isEditing = Boolean(props.module);

  const [name, setName] = useState(attributes?.name ?? '');
  const [description, setDescription] = useState(attributes?.description ?? '');
  const [campusID, setCampusID] = useState(attributes?.campusID ?? '');
  const [categoryID, setCategoryID] = useState(attributes?.categoryID ?? '');
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // A picked file previews from a local object URL; an existing module falls
  // back to whatever the API already has.
  const [pickedThumbnailURL, setPickedThumbnailURL] = useState<string | null>(null);

  useEffect(() => {
    if (!thumbnailFile) {
      setPickedThumbnailURL(null);
      return;
    }

    const objectURL = URL.createObjectURL(thumbnailFile);
    setPickedThumbnailURL(objectURL);

    return () => {
      URL.revokeObjectURL(objectURL);
    };
  }, [thumbnailFile]);

  const thumbnailURL = pickedThumbnailURL ?? attributes?.thumbnailURL ?? null;

  // A new Module needs its required fields filled in; an existing one needs an
  // actual change to save.
  const meetsRequirements = name.trim().length > 0 && campusID.length > 0 && categoryID.length > 0;
  const hasChanges =
    name !== (attributes?.name ?? '') ||
    description !== (attributes?.description ?? '') ||
    campusID !== (attributes?.campusID ?? '') ||
    categoryID !== (attributes?.categoryID ?? '') ||
    thumbnailFile !== null;
  const canSave = meetsRequirements && (!isEditing || hasChanges) && !isSaving;

  const saveModule = async () => {
    if (!auth.churchId) {
      setError('Missing church context. Please refresh and try again.');
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      let uploadedThumbnailURL = attributes?.thumbnailURL ?? null;

      if (thumbnailFile) {
        const uploadTargetResponse = await TrainingController.createThumbnailUploadTarget(
          auth.churchId,
          thumbnailFile.type || 'application/octet-stream',
          thumbnailFile.size,
          auth
        );

        if (!uploadTargetResponse.body) {
          setError(uploadTargetResponse.error || 'Unable to create an upload target for the thumbnail.');
          setIsSaving(false);
          return;
        }

        const uploadSucceeded = await NetworkController.performSignedPost(
          {
            url: uploadTargetResponse.body.url,
            fields: uploadTargetResponse.body.fields
          },
          thumbnailFile,
          thumbnailFile.type
        );

        if (!uploadSucceeded) {
          setError('The thumbnail upload failed. Please try again.');
          setIsSaving(false);
          return;
        }

        uploadedThumbnailURL = uploadTargetResponse.body.assetURL;
      }

      const savedAttributes: TrainingModuleAttributes = {
        name: name.trim(),
        description: description.trim() || null,
        thumbnailURL: uploadedThumbnailURL,
        campusID,
        categoryID
      };

      const response = props.module ?
        await TrainingController.updateTrainingModule(auth.churchId, props.module.id, savedAttributes, auth) :
        await TrainingController.createTrainingModule(auth.churchId, savedAttributes, auth);

      if (response.error) {
        setError(response.error);
        setIsSaving(false);
        return;
      }

      props.onSaved();
    } finally {
      setIsSaving(false);
    }
  };

  return <Modal
    id='module-editor-modal'
    isOpen={props.isOpen}
    footerButtons={[]}>
    <div className='module-editor-modal-content'>
      <div className='header'>
        <button
          type='button'
          className='header-control close'
          disabled={isSaving}
          onClick={props.close}>
          <img src={closeIcon} alt='Close icon' />
        </button>

        <h3 className='title'>{isEditing ? 'Edit Module' : 'New Module'}</h3>

        <button
          type='button'
          className={isSaving ? 'header-control confirm saving' : 'header-control confirm'}
          disabled={!canSave}
          onClick={() => {
            void saveModule();
          }}>
          {isSaving ?
            <span className='header-control-spinner' role='status' aria-label='Saving' /> :
            <img src={checkmarkIcon} alt='Save icon' />}
        </button>
      </div>

      <div className='body'>
        {error ? <p className='save-error'>{error}</p> : null}

        <div className='field-container'>
          <label htmlFor='module-name'>Title</label>
          <input
            id='module-name'
            type='text'
            value={name}
            placeholder='Module Title'
            disabled={isSaving}
            onChange={(e) => setName(e.target.value)}
          />
        </div>

        <div className='field-container'>
          <label htmlFor='module-description'>Description</label>
          <textarea
            id='module-description'
            rows={2}
            value={description}
            placeholder='What this module covers'
            disabled={isSaving}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div className='field-container'>
          <label htmlFor='module-thumbnail'>Thumbnail</label>

          <div className='thumbnail-picker'>
            {thumbnailURL ? <img className='thumbnail-preview' src={thumbnailURL} alt='' aria-hidden='true' /> :
              <div className='thumbnail-preview empty' aria-hidden='true' />}

            <div className='thumbnail-picker-controls'>
              <input
                id='module-thumbnail'
                type='file'
                accept='image/*'
                disabled={isSaving}
                onChange={(e) => setThumbnailFile(e.target.files?.[0] ?? null)}
              />

              {thumbnailFile ? <button
                type='button'
                className='thumbnail-clear'
                disabled={isSaving}
                onClick={() => setThumbnailFile(null)}>
                {attributes?.thumbnailURL ? 'Keep current image' : 'Clear'}
              </button> : null}
            </div>
          </div>
        </div>

        <div className='field-container'>
          <label htmlFor='module-campus'>Campus</label>
          <select
            id='module-campus'
            value={campusID}
            disabled={isSaving}
            onChange={(e) => setCampusID(e.target.value)}>
            <option value='' disabled>Select a campus</option>
            {churchData.campuses.map((campus) => (
              <option key={campus.id} value={campus.id}>
                {campus.attributes.name}
              </option>
            ))}
          </select>
        </div>

        <div className='field-container'>
          <label htmlFor='module-category'>Category</label>
          <select
            id='module-category'
            value={categoryID}
            disabled={isSaving}
            onChange={(e) => setCategoryID(e.target.value)}>
            <option value='' disabled>Select a category</option>
            {churchData.categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  </Modal>
}
